'use client';

import { Flex } from '@chakra-ui/react';
import { ReactFlowProvider } from '@xyflow/react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useBreadcrumbLabel } from '@features/navigation/BreadcrumbContext';

import { type CatalogItem, PickerMode, TriggerType, WorkflowNodeType } from './types';
import { type InitialGraph, useWorkflowStore } from './useWorkflowStore';
import WorkflowCanvas from './WorkflowCanvas';
import { type WorkflowCallbacks, WorkflowCallbacksProvider } from './WorkflowContext';
import WorkflowHeader from './WorkflowHeader';
import WorkflowSidebar from './WorkflowSidebar';

interface Props {
  workflowId?: string;
  initialGraph?: InitialGraph;
}

const TRIGGER_TYPE_VALUES = new Set<string>(Object.values(TriggerType));

const WorkflowBuilderInner = ({ workflowId, initialGraph }: Props) => {
  const { nodes, edges, meta, pickerState, configNodeId, actions, getNode } =
    useWorkflowStore(workflowId, initialGraph);

  useBreadcrumbLabel('Workflow');

  const searchParams = useSearchParams();
  const router = useRouter();
  const initialViewRef = useRef<'picker' | 'list' | null>(null);

  useEffect(() => {
    const triggerParam = searchParams.get('trigger');
    const pickerParam = searchParams.get('picker');
    const viewParam = searchParams.get('view');

    if (triggerParam == null && pickerParam == null && viewParam == null) {
      return;
    }

    if (triggerParam != null && TRIGGER_TYPE_VALUES.has(triggerParam)) {
      actions.addTriggerNode(triggerParam as TriggerType);
    } else if (pickerParam === 'triggers') {
      initialViewRef.current = 'picker';
    } else if (viewParam === 'list') {
      initialViewRef.current = 'list';
    }

    router.replace(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = useCallback(() => {
    // Save will be wired to API later
  }, []);

  const handleSidebarAddNode = useCallback(
    (item: CatalogItem) => {
      if (item.nodeType === WorkflowNodeType.Trigger && item.triggerType != null) {
        actions.addTriggerNode(item.triggerType);
      } else if (item.nodeType === WorkflowNodeType.Action && item.actionType != null) {
        actions.addActionNode(item.actionType);
      } else if (item.nodeType === WorkflowNodeType.Routing) {
        actions.addRoutingNode();
      } else if (item.nodeType === WorkflowNodeType.PaymentLink) {
        // Open config straight away so the user links/creates the link.
        const id = actions.addPaymentLinkNode();
        actions.openConfig(id);
      } else if (item.nodeType === WorkflowNodeType.SupplierPayout) {
        actions.addSupplierPayoutNode();
      }
    },
    [actions],
  );

  const handleCloseCanvasPopovers = useCallback(() => {
    actions.closePicker();
    actions.closeConfig();
  }, [actions]);

  const callbacks = useMemo<WorkflowCallbacks>(
    () => ({
      onOpenPicker: (nodeId: string, mode: PickerMode) => {
        actions.openPicker({ mode, anchorNodeId: nodeId });
      },
      onOpenConfig: (nodeId: string) => {
        actions.openConfig(nodeId);
      },
      onEdgeInsert: (edgeId: string, position: { x: number; y: number }) => {
        actions.openPicker({ mode: PickerMode.InsertOnEdge, anchorEdgeId: edgeId, position });
      },
      onDeleteNode: actions.removeNode,
      onAddRoutingPath: actions.addRoutingPath,
      onRemoveRoutingPath: actions.removeRoutingPath,
      onAddSupplierBranch: actions.addSupplierBranch,
      onUpdateNodeData: actions.updateNodeData,
    }),
    [actions],
  );

  return (
    <WorkflowCallbacksProvider value={callbacks}>
      <Flex direction="column" h="full" w="full">
        <WorkflowHeader
          name={meta.name}
          isDirty={meta.isDirty}
          onNameChange={actions.setName}
          onSave={handleSave}
          onDiscard={actions.discard}
        />
        <Flex flex={1} overflow="hidden">
          <WorkflowSidebar
            onAddNode={handleSidebarAddNode}
            onCloseCanvasPopovers={handleCloseCanvasPopovers}
            initialView={initialViewRef.current}
          />
          <WorkflowCanvas
            nodes={nodes}
            edges={edges}
            pickerState={pickerState}
            configNodeId={configNodeId}
            getNode={getNode}
            actions={actions}
          />
        </Flex>
      </Flex>
    </WorkflowCallbacksProvider>
  );
};

const WorkflowBuilder = ({ workflowId, initialGraph }: Props) => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner workflowId={workflowId} initialGraph={initialGraph} />
    </ReactFlowProvider>
  );
};

export default WorkflowBuilder;
